'use client'

import { useState, useEffect, useCallback, useRef } from 'react'

import { Box } from '@mui/joy'
import { keyframes } from '@emotion/react'
import color from '@ui/style/color.theme'
import UIFlex from '@components/ui/UIFlex'
import pxToRem from '@utils/pxToRem'
import MuiLink from '@mui/joy/Link'
import Button from '@mui/joy/Button'
import UISnsList from './UISnsList'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Mousewheel, Scrollbar } from 'swiper/modules'
import { Swiper as SwiperType } from 'swiper/types'
import { querySnsXList } from '@services/api/SNS/X'

import IconLoadingSpinner from 'src/assets/icons/IconLoadingSpinner'
import 'swiper/css/scrollbar'
import { useDarkModeControl } from '@hooks/useDarkModeControl'
import { PageVoSnsPostXResponseVo, SnsPostXResponseVo } from '@schemas/non-auth'

interface UISnsProps {
	data: PageVoSnsPostXResponseVo
}

const UISns = ({ data }: UISnsProps) => {
	const [isLoading, setIsLoading] = useState(false)
	const [isInitialLoad, setIsInitialLoad] = useState(true)
	const [pageNo, setPageNo] = useState(2)
	const [hasMoreData, setHasMoreData] = useState(true)
	const latestSnsXList = data
	const [slides, setSlides] = useState<SnsPostXResponseVo[]>(latestSnsXList?.list || [])
	const progressTimerRef = useRef<NodeJS.Timeout | null>(null)
	const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null)
	const swiperRef = useRef<SwiperType | null>(null)
	const [refreshing, setRefreshing] = useState(false)
	const [isManualRefresh, setIsManualRefresh] = useState(false)
	const [buttonDisabled, setButtonDisabled] = useState(false)

	const { darkMode } = useDarkModeControl()
	const spin = keyframes`
		from {
			transform : rotate(0);
		}
		to {
			transform: rotate(360deg);
		}
	`
	useEffect(() => {
		const newList = latestSnsXList?.list

		if (newList && newList.length > 0) {
			setSlides((prevSlides) => {
				if (prevSlides.length === 0) {
					return [...newList]
				}

				const existingIds = new Set(prevSlides.map((slide) => slide.snsId))
				const filteredNewSlides = newList.filter((slide) => !existingIds.has(slide.snsId))

				return [...filteredNewSlides, ...prevSlides]
			})
			setIsInitialLoad(false)
		}
	}, [latestSnsXList])

	const loadMoreData = useCallback(async () => {
		if (isLoading || isInitialLoad || !hasMoreData) return
		setIsLoading(true)
		try {
			const response = await querySnsXList({
				pageNo,
				pageSize: 10,
			}).queryFn()

			if (response?.data?.list && response.data.list.length > 0) {
				setSlides((prevSlides) => {
					const existingIds = new Set(prevSlides.map((slide) => slide.snsId))
					const filteredNewSlides =
						response?.data?.list?.filter(
							(slide: SnsPostXResponseVo) => !existingIds.has(slide.snsId),
						) || []
					return [...prevSlides, ...filteredNewSlides]
				})
				setPageNo((prevPageNo) => prevPageNo + 1)
			} else {
				setHasMoreData(false)
			}
		} finally {
			setIsLoading(false)
			setIsInitialLoad(false)
		}
	}, [isLoading, isInitialLoad, pageNo, hasMoreData])

	const handleProgress = useCallback(
		(swiper: SwiperType) => {
			if (swiper.progress > 0.8 && hasMoreData) {
				if (progressTimerRef.current) {
					clearTimeout(progressTimerRef.current)
				}

				progressTimerRef.current = setTimeout(() => {
					loadMoreData()
					progressTimerRef.current = null
				}, 100)
			}
		},
		[loadMoreData, hasMoreData],
	)

	const resetData = useCallback(
		async (showLoading = true) => {
			if (progressTimerRef.current) {
				clearTimeout(progressTimerRef.current)
				progressTimerRef.current = null
			}

			setIsLoading(true)
			if (showLoading) {
				setRefreshing(true)
				setButtonDisabled(true)
			}

			try {
				const response = await querySnsXList({
					pageNo: 1,
					pageSize: 10,
				}).queryFn()

				if (response?.data?.list && response.data.list.length > 0) {
					setSlides([...response.data.list])
				} else {
					setSlides([])
				}
				setPageNo(2)
				setIsInitialLoad(false)
				setHasMoreData(true)
			} catch (error) {
				if (latestSnsXList) {
					setSlides([...latestSnsXList.list])
				}
			} finally {
				setIsLoading(false)
				if (swiperRef.current) {
					swiperRef.current.slideTo(0, 0)
					swiperRef.current.update()
				}
				if (showLoading) {
					setTimeout(() => {
						setRefreshing(false)
						setButtonDisabled(false)
					}, 1000)
				}
			}
		},
		[latestSnsXList],
	)

	const handleManualRefresh = useCallback(() => {
		setIsManualRefresh(true)
		resetData(true)
		setTimeout(() => {
			setIsManualRefresh(false)
		}, 500)
	}, [resetData])

	useEffect(() => {
		if (autoRefreshTimerRef.current) {
			clearInterval(autoRefreshTimerRef.current)
			autoRefreshTimerRef.current = null
		}

		autoRefreshTimerRef.current = setInterval(() => {
			if (swiperRef.current && swiperRef.current.progress === 0) {
				resetData(false)
			}
		}, 1000 * 60)

		return () => {
			if (progressTimerRef.current) {
				clearTimeout(progressTimerRef.current)
			}
			if (autoRefreshTimerRef.current) {
				clearInterval(autoRefreshTimerRef.current)
				autoRefreshTimerRef.current = null
			}
		}
	}, [resetData])

	return (
		<UIFlex
			flexDirection="column"
			height={{ xl: pxToRem(507), lg: pxToRem(426), md: pxToRem(374), xs: pxToRem(374) }}
			border={1}
			borderColor="divider"
			sx={{
				backgroundColor: color.colWhite,
				'& .swiper-scrollbar': {
					backgroundColor: color.colWhite,
				},
				'& .swiper-scrollbar-drag': {
					width: pxToRem(4),
					backgroundColor: color.colGray3,
				},
			}}
		>
			<UIFlex
				padding={pxToRem(16)}
				alignItems="stretch"
				flexDirection="row"
				borderBottom={1}
				borderColor="divider"
			>
				<UIFlex
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
					gap={pxToRem(8)}
				>
					<MuiLink
						href="https://x.com/Jtbc_Factcheck"
						target="_blank"
						underline="none"
						sx={{
							fontWeight: 700,
							color: color.colBlack,
							fontSize: pxToRem(18),
							lineHeight: pxToRem(20),
							'&:hover': {
								color: color.colGray5,
							},
						}}
					>
						2025 대선 팩트체크
					</MuiLink>
					<Button
						type="button"
						variant="outlined"
						onClick={handleManualRefresh}
						disabled={buttonDisabled}
						sx={{
							width: pxToRem(24),
							height: pxToRem(24),
							minWidth: pxToRem(24),
							minHeight: pxToRem(24),
							padding: pxToRem(0),
							border: `1px solid ${color.colGray3}`,
							borderRadius: '20px',
							backgroundImage: `url(${'/images/icon/iconRefresh.svg'})`,
							backgroundSize: '20px',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
							backgroundColor: 'transparent',
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
					/>
				</UIFlex>
			</UIFlex>

			<Box sx={{ flex: 1, overflow: 'hidden' }}>
				<Swiper
					style={{ height: '100%', padding: `0 ${pxToRem(16)}` }}
					direction="vertical"
					slidesPerView="auto"
					modules={[FreeMode, Mousewheel, Scrollbar]}
					freeMode
					mousewheel
					scrollbar={{
						draggable: true,
					}}
					onProgress={handleProgress}
					onSwiper={(swiper) => {
						swiperRef.current = swiper
					}}
				>
					{refreshing && isManualRefresh ? (
						<SwiperSlide style={{ height: '100%' }}>
							<UIFlex
								justifyContent="center"
								alignItems="center"
								width="100%"
								height="100%"
								sx={{
									position: 'relative',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									background: darkMode ? color.colBlack : color.colWhite,
									svg: {
										animation: `${spin} 0.5s infinite linear`,
									},
								}}
							>
								<IconLoadingSpinner />
							</UIFlex>
						</SwiperSlide>
					) : (
						slides?.map((snsPost, index) => (
							<SwiperSlide
								key={snsPost.snsId}
								style={{
									height: 'auto',
								}}
							>
								<UISnsList snsPost={snsPost} isLast={index === slides.length - 1} />
							</SwiperSlide>
						))
					)}
				</Swiper>
			</Box>
		</UIFlex>
	)
}

export default UISns
